import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@repo/utils";

const buttonVariants = cva(
  [
    // Pastille : rayon plein sur tous les boutons, y compris les boutons
    // icone, qui deviennent des ronds.
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-controle text-sm font-medium",
    // Relief : le bouton se souleve au survol et s'enfonce a l'appui. La duree
    // est courte (150ms) pour que l'interface reste nerveuse sous la souris.
    "transition-all duration-150 ease-out will-change-transform",
    "active:translate-y-0 active:shadow-none",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
    "outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
    "aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-charte-button-hover hover:shadow-md hover:-translate-y-px",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 hover:shadow-md hover:-translate-y-px focus-visible:ring-destructive/20",
        outline:
          "border border-charte-line-strong bg-card shadow-xs hover:border-charte-line-accent hover:bg-charte-hover-strong hover:text-accent-foreground hover:shadow-sm hover:-translate-y-px",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-charte-accent-light hover:shadow-md hover:-translate-y-px",
        ghost:
          "hover:bg-charte-hover-strong hover:text-accent-foreground active:bg-charte-wash-accent-strong",
        // Pas de relief sur un lien : il n'a pas de surface a soulever.
        link: "text-primary underline-offset-4 hover:text-charte-accent-light hover:underline active:translate-y-0",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export { Button, buttonVariants };
